/* eslint-disable no-console */
/**
 * Composable useSidePanel
 *
 * API pública para gerenciar painéis laterais
 * Fornece métodos para abrir, fechar e comunicar entre níveis
 */
import { computed, inject, ref, isRef, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { confirm } from '@/components/modal/confirmHelper'
import {
  appendLevel,
  buildHash,
  removeLevelsFrom,
  parseHash,
} from '@/components/sidepanel/hashParser'
import { validatePanelName } from '@/components/sidepanel/registry'

// Estado singleton no nível do módulo — compartilhado entre todas as instâncias
/**
 * Se algum safado vier me questionar por que eu não botei isso na store, seguem as razões:
 * Não é estado de aplicação — é infraestrutura interna de comunicação entre módulos.
 * Maps não se encaixam bem no modelo do Pinia (feito para objetos/arrays simples).
 * beforeCloseHooks ficou de fora de qualquer forma (funções não são serializáveis).
 * Pinia DevTools vai poluir o painel de estado com dados que não são relevantes para o desenvolvedor
 * não dá pra usar algo da store direto como ref, tem que usar storeToRefs,
 * o que prova que não estaria certo...
 *
 * store é uma praga!
 */
const callbacksStore = ref(new Map())

export { callbacksStore }

/**
 * Composable principal para gerenciar sidepanels
 * @returns {object} API de controle de painéis
 */
export function useSidePanel() {
  const router = useRouter()
  const route = useRoute()

  // Contextos injetados pelo SidePanelManager (via provide pois panelContexts é computado lá)
  const panelContexts = inject('sidePanelContexts', null)

  // Nível estático injetado pelo PanelLevelProvider — cada painel recebe o seu próprio.
  // Fora de um painel (ex: página pai abrindo via open()), não há provide → null.
  const injectedLevel = inject('currentPanelLevel', null)

  // Determinar nível atual:
  // - Dentro de um painel: usa o nível injetado (estável, não muda quando outros painéis abrem)
  // - Fora de um painel (chamador como Overview.vue): usa o total de níveis no hash
  const currentLevelFromHash = computed(() => {
    if (injectedLevel !== null) return injectedLevel
    const levels = parseHash(route.hash)
    return levels.length
  })

  // Context do nível atual
  const currentContext = computed(() => {
    if (!panelContexts?.value) return null
    return panelContexts.value[currentLevelFromHash.value] || null
  })

  /**
   * Abre um novo painel (próximo nível)
   * @param {string} panelName - Nome do painel no registry
   * @param {object} props - Props para o componente
   * @param {object} options - Opções adicionais
   * @param {string} options.title - Título customizado
   * @param {Function} options.beforeOpen - Validação antes de abrir (async, retorna boolean)
   * @param {Function} options.onClose - Callback quando painel fecha
   * @param {Function} options.beforeClose - Validação antes de fechar
   * @param {object} options.context - Contexto compartilhado com filho
   */
  const open = async (panelName, props = {}, options = {}) => {
    // Validar painel existe
    if (!validatePanelName(panelName)) {
      console.error(`[useSidePanel] Cannot open panel "${panelName}" - not registered`)
      return
    }

    // Executar beforeOpen se fornecido — aborta se retornar false
    if (options.beforeOpen) {
      try {
        const canOpen = await options.beforeOpen()
        if (!canOpen) return
      } catch (error) {
        console.error('[useSidePanel] beforeOpen hook error:', error)
        return
      }
    }

    // Separar props em serializáveis (para URL) e funções (para callbacksStore)
    const serializableProps = {}
    const functionProps = {}

    for (const [key, value] of Object.entries(props)) {
      if (typeof value === 'function') {
        functionProps[key] = value
      } else {
        serializableProps[key] = value
      }
    }

    // Construir novo hash com nível adicional (apenas props serializáveis)
    const newHash = appendLevel(route.hash, panelName, serializableProps)

    // Armazenar callbacks, options e function props no store global
    if (callbacksStore) {
      const levels = parseHash(newHash)
      const newLevel = levels.length

      callbacksStore.value.set(newLevel, {
        onClose: options.onClose,
        beforeClose: options.beforeClose,
        title: options.title,
        context: options.context,
        functionProps, // Store function props here
      })
    }

    // Navegar para novo hash (preservando path e query)
    router.push({
      path: route.path,
      query: route.query,
      hash: newHash,
    })
  }

  /**
   * Fecha o painel atual
   * @param {any} data - Dados para retornar ao pai via callback
   */
  const close = async (data = null, { force = false } = {}) => {
    const currentLevel = currentLevelFromHash.value

    if (!currentLevel) {
      console.warn('[useSidePanel] Cannot close - not inside a panel context')
      return
    }

    // Recuperar callbacks do nível atual
    const callbacks = callbacksStore?.value.get(currentLevel)

    // Verificar beforeClose hook (apenas se não for force)
    const beforeClose = callbacks?.beforeClose
    if (!force && beforeClose) {
      try {
        const canClose = await beforeClose()
        if (!canClose) {
          return
        }
      } catch (error) {
        console.error('[useSidePanel] beforeClose hook error:', error)
        return
      }
    }

    // Capturar onClose antes de limpar callbacks
    const onCloseCb = callbacks?.onClose

    // Remover este nível e todos acima dele
    const newHash = removeLevelsFrom(route.hash, currentLevel)

    // Limpar callbacks deste nível e de todos os níveis acima (que também foram removidos do hash)
    if (callbacksStore) {
      const totalBefore = parseHash(route.hash).length
      for (let i = currentLevel; i <= totalBefore; i++) {
        callbacksStore.value.delete(i)
      }
    }

    // Navegar primeiro — o hash precisa estar atualizado antes do onClose
    await router.replace({
      path: route.path,
      query: route.query,
      hash: newHash,
    })

    // Executar callback onClose depois da navegação
    // Assim qualquer close() chamado dentro do callback já enxerga o hash correto
    if (onCloseCb) {
      onCloseCb(data)
    }
  }

  /**
   * Fecha todos os painéis ignorando todos os hooks (force close)
   */
  const closeAll = () => {
    callbacksStore.value.clear()

    router.replace({
      path: route.path,
      query: route.query,
      hash: '',
    })
  }

  /**
   * Fecha todos os painéis chamando os onClose de cada nível (do topo para a raiz)
   * e respeitando beforeClose em cada nível. Se qualquer beforeClose retornar false, aborta.
   * @param {any} data - Dados passados para o onClose do nível mais alto
   */
  const closeStack = async (data = null) => {
    const levels = parseHash(route.hash)

    // Verificar beforeClose do topo para a raiz — aborta se qualquer um negar
    for (let i = levels.length; i >= 1; i--) {
      const callbacks = callbacksStore.value.get(i)
      if (callbacks?.beforeClose) {
        try {
          const canClose = await callbacks.beforeClose()
          if (!canClose) return
        } catch (error) {
          console.error(`[useSidePanel] beforeClose error at level ${i}:`, error)
          return
        }
      }
    }

    // Todos permitiram — chamar onClose do topo para a raiz
    for (let i = levels.length; i >= 1; i--) {
      const callbacks = callbacksStore.value.get(i)
      if (callbacks?.onClose) {
        try {
          callbacks.onClose(i === levels.length ? data : null)
        } catch (error) {
          console.error(`[useSidePanel] onClose error at level ${i}:`, error)
        }
      }
    }

    callbacksStore.value.clear()

    router.replace({
      path: route.path,
      query: route.query,
      hash: '',
    })
  }

  /**
   * Registra um hook beforeClose para o painel atual
   * @param {Function} hook - Função async que retorna boolean (true = pode fechar, false = cancela)
   * @example
   * setBeforeClose(async () => {
   *   if (!hasChanges.value) return true
   *   return await showConfirmDialog('Descartar alterações?')
   * })
   */
  const setBeforeClose = hook => {
    const currentLevel = currentLevelFromHash.value

    if (!currentLevel) {
      console.warn('[useSidePanel] Cannot set beforeClose - not inside a panel context')
      return
    }

    if (typeof hook !== 'function') {
      console.error('[useSidePanel] beforeClose must be a function')
      return
    }

    // Atualizar entry existente no callbacksStore (fonte única de verdade)
    const existing = callbacksStore.value.get(currentLevel) || {}
    callbacksStore.value.set(currentLevel, { ...existing, beforeClose: hook })
  }

  /**
   * Substitui o painel atual (mesmo nível)
   * @param {string} panelName - Novo painel
   * @param {object} props - Props
   */
  const replace = (panelName, props = {}) => {
    if (!validatePanelName(panelName)) {
      console.error(`[useSidePanel] Cannot replace with panel "${panelName}" - not registered`)
      return
    }

    const currentLevel = currentLevelFromHash.value || 1
    const levels = parseHash(route.hash)

    // Substituir apenas o nível atual
    levels[currentLevel - 1] = {
      level: currentLevel,
      name: panelName,
      props,
    }

    router.replace({
      path: route.path,
      query: route.query,
      hash: buildHash(levels),
    })
  }

  /**
   * Solicita refresh do painel pai
   * @param {object} data - Metadados do refresh
   */
  const requestParentRefresh = (data = {}) => {
    const currentLevel = currentLevelFromHash.value

    if (!currentLevel || currentLevel === 1) {
      console.warn('[useSidePanel] Cannot request parent refresh - no parent')
      return
    }

    // Emitir evento customizado que o pai pode escutar
    window.dispatchEvent(
      new CustomEvent('sidepanel:refresh-request', {
        detail: {
          fromLevel: currentLevel,
          data,
        },
      }),
    )
  }

  // === COMPUTEDS ===

  /**
   * Acessa contexto do pai (read-only)
   */
  const parentContext = computed(() => currentContext.value?.parent || null)

  /**
   * Acessa contexto raiz (nível 1)
   */
  const rootContext = computed(() => currentContext.value?.root || null)

  /**
   * Contexto próprio do painel
   */
  const ownContext = computed(() => currentContext.value?.own || null)

  /**
   * Nível atual (1, 2, 3)
   */
  const currentLevel = computed(() => currentContext.value?.own?.level || 0)

  /**
   * Total de níveis abertos
   */
  const totalLevels = computed(() => {
    const levels = parseHash(route.hash)
    return levels.length
  })

  /**
   * Verifica se é o último nível (mais alto)
   */
  const isTopLevel = computed(() => {
    return currentLevel.value === totalLevels.value
  })

  /**
   * Verifica se é o primeiro nível (raiz)
   */
  const isRootLevel = computed(() => currentLevel.value === 1)

  return {
    // Navegação
    open,
    close,
    closeAll,
    closeStack,
    replace,

    // Hooks
    setBeforeClose,

    // Comunicação
    requestParentRefresh,
    parentContext,
    rootContext,
    ownContext,

    // Informações
    currentLevel,
    totalLevels,
    isTopLevel,
    isRootLevel,
  }
}

/**
 * Helper para proteger formulários contra perda de dados acidentais.
 * Configura automaticamente um beforeClose que verifica alterações e exibe confirmação.
 *
 * @param {Object} options - Opções de configuração
 * @param {import('vue').Ref<boolean>|Function} options.hasUnsavedChanges - Ref ou função que retorna se há alterações não salvas
 * @param {import('vue').Ref<boolean>} [options.hasCompletedOperation] - Ref que indica se a operação foi concluída com sucesso
 * @param {Object} [options.confirmOptions] - Opções customizadas para o diálogo de confirmação
 * @param {string} [options.confirmOptions.title='Descartar alterações?'] - Título do diálogo
 * @param {string} [options.confirmOptions.message] - Mensagem do diálogo
 * @param {string} [options.confirmOptions.confirmLabel='Sim, descartar'] - Label do botão de confirmação
 * @param {'warning'|'danger'|'info'|'success'} [options.confirmOptions.variant='warning'] - Variante do diálogo
 *
 * @returns {Object} - Objeto com métodos auxiliares
 * @returns {import('vue').Ref<boolean>} hasCompletedOperation - Ref para marcar operação como concluída
 *
 * @example
 * // Uso básico com vee-validate meta.dirty
 * const { meta } = useForm({ validationSchema })
 * useFormBeforeClose({
 *   hasUnsavedChanges: () => meta.value.dirty
 * })
 *
 * @example
 * // Uso com operação bem-sucedida
 * const { hasCompletedOperation } = useFormBeforeClose({
 *   hasUnsavedChanges: () => meta.value.dirty
 * })
 *
 * const submit = handleSubmit(async (values) => {
 *   await saveData(values)
 *   hasCompletedOperation.value = true
 *   close()
 * })
 */
export function useFormBeforeClose(options = {}) {
  const {
    hasUnsavedChanges,
    hasCompletedOperation: externalCompletedOperation,
    confirmOptions = {},
  } = options

  const { close, setBeforeClose } = useSidePanel()
  const internalCompletedOperation = ref(false)

  // Usar a ref externa se fornecida, senão usar a interna
  const hasCompletedOperation = externalCompletedOperation || internalCompletedOperation

  // Opções padrão do diálogo de confirmação
  const defaultConfirmOptions = {
    title: 'Descartar alterações?',
    message: 'Você possui alterações não salvas. Deseja realmente sair?',
    variant: 'warning',
    confirmLabel: 'Sim, descartar',
    ...confirmOptions,
  }

  /**
   * Verifica se há alterações não salvas.
   * Suporta tanto Ref quanto função.
   */
  const checkHasUnsavedChanges = () => {
    if (!hasUnsavedChanges) return false

    if (isRef(hasUnsavedChanges)) {
      return hasUnsavedChanges.value
    }

    if (typeof hasUnsavedChanges === 'function') {
      return hasUnsavedChanges()
    }

    return false
  }

  /**
   * Hook executado antes de QUALQUER fechamento do painel
   * (botão X, clicar fora, ESC, navegação por hash, etc)
   */
  const handleBeforeClose = async () => {
    // Se a operação foi concluída com sucesso, fecha e propaga sucesso
    if (hasCompletedOperation.value) {
      close({ success: true }, { force: true })
      return false // Previne close padrão, já fechamos manualmente
    }

    // Se não há alterações não salvas, permite fechar normalmente
    if (!checkHasUnsavedChanges()) {
      return true
    }

    // Exibe diálogo de confirmação
    const shouldClose = await confirm(defaultConfirmOptions)

    return shouldClose
  }

  // Registra o hook de beforeClose
  onMounted(() => {
    setBeforeClose(handleBeforeClose)
  })

  return {
    hasCompletedOperation,
  }
}
